/**
 * Redux Actions for Moodle E-Learning Courses
 *
 * Handles fetching e-learning courses from Moodle
 * Uses operation status pattern from @uncinc/react-drupal-core
 */

import {
  setOperationStatus,
  UI_OPERATION_STATUS_REQUEST,
  UI_OPERATION_STATUS_SUCCESS,
  UI_OPERATION_STATUS_ERROR,
} from '@uncinc/react-drupal-core';
import config from 'react-global-configuration';
import type { Dispatch } from 'redux';
import type { MoodleCoursesApiResponse } from '../../../types/moodle-courses';

// Action types
export const SET_MOODLE_COURSES_DATA = 'SET_MOODLE_COURSES_DATA';
export const SET_MOODLE_COURSES_LOADING = 'SET_MOODLE_COURSES_LOADING';
export const SET_MOODLE_COURSES_ERROR = 'SET_MOODLE_COURSES_ERROR';
export const CLEAR_MOODLE_COURSES_ERROR = 'CLEAR_MOODLE_COURSES_ERROR';

// Helper function to get API URL
const getApiUrl = (): string => {
  const baseUrl = config.get('API_URL') || '/api';
  if (baseUrl.startsWith('//')) {
    return baseUrl.replace('//', 'http://');
  }
  return baseUrl;
};

/**
 * Set loading state
 */
export const setMoodleCoursesLoading = (isLoading: boolean) => ({
  type: SET_MOODLE_COURSES_LOADING,
  payload: isLoading,
});

/**
 * Set error state
 */
export const setMoodleCoursesError = (error: string | null) => ({
  type: SET_MOODLE_COURSES_ERROR,
  payload: error,
});

/**
 * Clear error state
 */
export const clearMoodleCoursesError = () => ({
  type: CLEAR_MOODLE_COURSES_ERROR,
});

/**
 * Fetch user's Moodle courses
 *
 * GET /api/user/moodle-courses
 * Returns array of courses the user has access to
 */
export const fetchMoodleCourses = () => async (dispatch: Dispatch) => {
  dispatch(setOperationStatus('moodleCourses', 'fetch', UI_OPERATION_STATUS_REQUEST, null));
  dispatch(setMoodleCoursesLoading(true));
  dispatch(setMoodleCoursesError(null));

  try {
    const apiUrl = getApiUrl();
    const url = `${apiUrl}/api/user/moodle-courses`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Send cookies for authentication
    });

    // Handle authentication errors
    if (response.status === 403) {
      const error = 'UNAUTHENTICATED';
      dispatch(setMoodleCoursesError(error));
      dispatch(setMoodleCoursesLoading(false));
      dispatch(setOperationStatus('moodleCourses', 'fetch', UI_OPERATION_STATUS_ERROR, null));
      throw new Error(error);
    }

    if (!response.ok) {
      const error = `API error: ${response.status}`;
      dispatch(setMoodleCoursesError(error));
      dispatch(setMoodleCoursesLoading(false));
      dispatch(setOperationStatus('moodleCourses', 'fetch', UI_OPERATION_STATUS_ERROR, null));
      throw new Error(error);
    }

    const data: MoodleCoursesApiResponse = await response.json();

    // Validate response is an array
    if (!Array.isArray(data)) {
      const error = 'Invalid API response format';
      dispatch(setMoodleCoursesError(error));
      dispatch(setMoodleCoursesLoading(false));
      dispatch(setOperationStatus('moodleCourses', 'fetch', UI_OPERATION_STATUS_ERROR, null));
      throw new Error(error);
    }

    dispatch({
      type: SET_MOODLE_COURSES_DATA,
      payload: data,
    });

    dispatch(setMoodleCoursesLoading(false));
    dispatch(setOperationStatus('moodleCourses', 'fetch', UI_OPERATION_STATUS_SUCCESS, null));
  } catch (error) {
    console.error('Error fetching Moodle courses:', error);
    // Error state already set in specific handlers above
    // Just re-throw for component-level handling if needed
    throw error;
  }
};
